import { SagaIterator } from "redux-saga";
import { all, call, delay, put, takeEvery, takeLatest } from "redux-saga/effects";

import { EventPlace, pushDataLayerEvent, trackEngagement } from "helpers/analytics";
import { ApiManager } from "services/api";
import { EmailDomainSerializer } from "services/api/email-domain";
import { CRUDAction, IEntityCreateSuccess, IEntityDeleteSuccess, RequestAction } from "store/entities/actions";
import { IActionWithPayload } from "store/helper";

import { EMAIL_DOMAIN, EmailDomainActions, EmailDomainActionsNames } from "./actions";
import {
  ICreateEmailDomainPayload,
  IDeleteEmailDomainPayload,
  IEmailDomain,
  IFetchEmailDomainPayload,
  IVerifyEmailDomainPayload,
  IVerifySuccessDomainPayload,
} from "./interfaces";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchSingle(action: IActionWithPayload<string, IFetchEmailDomainPayload>) {
  const { id } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.emailDomainApi.fetch, id);

  if (response.result) {
    yield put(EmailDomainActions.fetchSuccess({ id, value: EmailDomainSerializer.deserialize(response.result) }));
    yield put(EmailDomainActions.fetchCollection({}));
  } else {
    yield put(EmailDomainActions.fetchFailure({ id, error: null }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchCollection() {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.emailDomainApi.fetchAll);

  if (response.result) {
    yield put(
      EmailDomainActions.fetchCollectionSuccess({
        values: EmailDomainSerializer.deserializeList(response.result),
      })
    );
  } else {
    yield put(EmailDomainActions.fetchCollectionFailure({ error: null }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
const backoff = (attempt: any) => {
  if (!attempt) {
    return attempt;
  }
  if (attempt === 1) {
    return 2000;
  }
  if (attempt === 2) {
    return 5000;
  }
  return 8000;
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
function* retryDomainRequest(requestFn: any, requestArg: any, backoff: any, maxAttempts: any) {
  let attempt = 0;
  let response;

  while ((!response || response.responseStatus === 422) && attempt < maxAttempts) {
    yield delay(backoff(attempt));
    attempt++;
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    response = yield call(requestFn, requestArg);
  }
  return response;
}

function* create(action: IActionWithPayload<string, ICreateEmailDomainPayload>): SagaIterator {
  const { payload } = action;
  const emailDomain = { ...payload.value };
  const { requestSource, skipRetry } = payload;
  const maxAttempts = skipRetry ? 1 : 6;
  const response = yield call(retryDomainRequest, ApiManager.emailDomainApi.create, emailDomain, backoff, maxAttempts);

  if (response.result) {
    const newEmailDomain = EmailDomainSerializer.deserialize(response.result);
    yield put(
      EmailDomainActions.createSuccess({
        id: newEmailDomain.id,
        value: newEmailDomain,
        requestSource,
      })
    );
  } else if (response.responseStatus === 422) {
    yield put(
      EmailDomainActions.createFailure({
        status: "unverified",
        name: emailDomain.name,
        id: emailDomain.name,
        error: response.error.error.message,
        errorObject: response.error.error,
        requestSource,
        flow: response.error.error.details.dcSyncURL ? "domainConnect" : "manualProvider",
      })
    );
  } else {
    yield put(
      EmailDomainActions.createFailure({
        error: response.error.error.message,
        errorObject: response.error.error,
        requestSource,
      })
    );
  }
}

function* deleteEmailDomain(action: IActionWithPayload<string, IDeleteEmailDomainPayload>): SagaIterator {
  const { id } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.emailDomainApi.deleteEmailDomain, id);
  if (response.result) {
    yield put(EmailDomainActions.deleteSuccess({ id }));
  } else {
    yield put(EmailDomainActions.deleteFailure({ id }));
  }
}

function* verifyEmailDomain(action: IActionWithPayload<string, IVerifyEmailDomainPayload>): SagaIterator {
  const { id, skipRetry } = action.payload;
  const maxAttempts = skipRetry ? 1 : 6;
  const response = yield call(retryDomainRequest, ApiManager.emailDomainApi.verify, id, backoff, maxAttempts);

  if (response.result) {
    yield put(EmailDomainActions.verifySuccess({ id }));
  } else {
    yield put(
      EmailDomainActions.verifyFailure({ id, error: response.error.error.message, errorObject: response.error.error })
    );
  }
}

function* verifyEmailDomainSuccess(action: IActionWithPayload<string, IVerifySuccessDomainPayload>): SagaIterator {
  yield call(trackEngagement, "Settings: Domain verification started", EventPlace.Settings, {
    emailDomainID: action.payload.id,
  });
}

function* createSuccess(action: IActionWithPayload<string, IEntityCreateSuccess<IEmailDomain>>): SagaIterator {
  yield call(trackEngagement, "Settings: Domain created", EventPlace.Settings, { emailDomainID: action.payload.id });
  yield call(pushDataLayerEvent, "Settings: Domain created");
}

function* deleteSuccess(action: IActionWithPayload<string, IEntityDeleteSuccess>): SagaIterator {
  yield call(trackEngagement, "Settings: Domain deleted", EventPlace.Settings, { emailDomainID: action.payload.id });
}

export function* emailDomainsSagas(): SagaIterator {
  yield all([
    takeLatest(EMAIL_DOMAIN[CRUDAction.FETCH_SINGLE][RequestAction.REQUEST], fetchSingle),
    takeLatest(EMAIL_DOMAIN[CRUDAction.FETCH_COLLECTION][RequestAction.REQUEST], fetchCollection),
    takeLatest(EMAIL_DOMAIN[CRUDAction.CREATE][RequestAction.REQUEST], create),
    takeEvery(EMAIL_DOMAIN[CRUDAction.CREATE][RequestAction.SUCCESS], createSuccess),
    takeEvery(EMAIL_DOMAIN[CRUDAction.DELETE][RequestAction.SUCCESS], deleteSuccess),
    takeLatest(EMAIL_DOMAIN[CRUDAction.DELETE][RequestAction.REQUEST], deleteEmailDomain),
    takeLatest(EmailDomainActionsNames.VERIFY, verifyEmailDomain),
    takeEvery(EmailDomainActionsNames.VERIFY_SUCCESS, verifyEmailDomainSuccess),
  ]);
}
